import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { messagingService } from '../../../services/messagingService';
import { useAuth } from '../../../contexts/AuthContext';
import { getTeamNotifications } from '../../../services/notificationService';
import NotificationsModal from '../../../components/ui/NotificationsModal';

const QuickActions = ({ project, team }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showProblemStatementModal, setShowProblemStatementModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [loadingContact, setLoadingContact] = useState(null);
  const [error, setError] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);

  // Load recent notifications from database
  useEffect(() => {
    if (team?.id) {
      loadRecentNotifications();
    }
  }, [team?.id]);

  const loadRecentNotifications = async () => {
    if (!team?.id) return;

    try {
      setLoadingNotifications(true);
      const { success, data } = await getTeamNotifications(team?.id, 5);

      if (success) {
        setNotifications(data || []);
      }
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleViewAllNotifications = () => {
    setShowNotificationsModal(true);
  };

  const handleContactManager = async () => {
    if (!team?.manager) {
      setError('No manager assigned to this team yet');
      return;
    }

    setLoadingContact('manager');
    setError('');

    try {
      // Find manager user by name from teams table
      const { data: managerUser, error: findError } = await messagingService?.getUserByName(team?.manager);
      
      if (findError || !managerUser) {
        setError(`Could not find manager: ${team?.manager}`);
        setLoadingContact(null);
        return;
      }

      // Create or find existing conversation with manager
      const { conversationId } = await messagingService?.createDirectConversation(managerUser?.id);
      
      // Navigate to team-workspace chat tab and auto-open conversation
      navigate('/team-workspace', { 
        state: { 
          activeTab: 'chat',
          autoOpenConversation: conversationId,
          contactType: 'manager',
          contactName: team?.manager
        } 
      });
    } catch (error) {
      console.error('Failed to start manager chat:', error);
      setError(error?.message || 'Failed to start conversation with manager');
    } finally {
      setLoadingContact(null);
    }
  };

  const handleContactCityOfficial = async () => {
    if (!team?.city_official) {
      setError('No city official assigned to this team yet');
      return;
    }

    setLoadingContact('city_official');
    setError('');

    try {
      // Find city official user by name from teams table
      const { data: officialUser, error: findError } = await messagingService?.getUserByName(team?.city_official_contact);
      
      if (findError || !officialUser) {
        setError(`Could not find city official: ${team?.city_official_contact}`);
        setLoadingContact(null);
        return;
      }

      // Create or find existing conversation with city official
      const { conversationId } = await messagingService?.createDirectConversation(officialUser?.id);
      
      // Navigate to team-workspace chat tab and auto-open conversation
      navigate('/team-workspace', { 
        state: { 
          activeTab: 'chat',
          autoOpenConversation: conversationId,
          contactType: 'city_official',
          contactName: team?.city_official_contact
        } 
      });
    } catch (error) {
      console.error('Failed to start city official chat:', error);
      setError(error?.message || 'Failed to start conversation with city official');
    } finally {
      setLoadingContact(null);
    }
  };

  const handleProblemStatement = () => {
    setShowProblemStatementModal(true);
  };

  const quickActionItems = [
    {
      id: 'learning-resources',
      title: 'Learning Resources',
      description: 'Access role-specific learning pathways and materials',
      icon: 'BookOpen',
      color: 'text-blue-600 bg-blue-50',
      action: () => window.open('https://classroom.google.com/', '_blank') 
    },
    {
      id: 'manager-communication',
      title: 'Contact Manager',
      description: 'Connect with your assigned manager for guidance',
      icon: 'GraduationCap',
      color: 'text-purple-600 bg-purple-50',
      action: handleContactManager,
      loading: loadingContact === 'manager',
      disabled: !team?.manager
    },
    {
      id: 'city-contact',
      title: 'City Official Contact',
      description: 'Reach out to the city point of contact',
      icon: 'Building2',
      color: 'text-green-600 bg-green-50',
      action: handleContactCityOfficial,
      loading: loadingContact === 'city_official',
      disabled: !team?.city_official_contact
    },
    {
      id: 'problem-details',
      title: 'Problem Statement',
      description: 'Review the full problem statement details',
      icon: 'FileText',
      color: 'text-orange-600 bg-orange-50',
      action: handleProblemStatement
    }
  ];

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'task_created': return 'Plus';
      case 'task_updated': return 'Edit';
      case 'task_deleted': return 'Trash2';
      case 'file_uploaded': return 'Upload';
      case 'meeting_scheduled': return 'Calendar';
      case 'meeting_reminder': return 'Clock';
      default: return 'Bell';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - notificationTime) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return notificationTime?.toLocaleDateString();
  };

  const getPriorityColor = (type) => {
    switch (type) {
      case 'task_deleted': return 'text-error bg-error/10 border-error/20';
      case 'task_updated': return 'text-warning bg-warning/10 border-warning/20';
      case 'task_created': case'file_uploaded': return 'text-primary bg-primary/10 border-primary/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-destructive">{error}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setError('')}
              className="text-destructive hover:bg-destructive/20"
            >
              <Icon name="X" size={14} />
            </Button>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      {/* <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {quickActionItems?.map((item) => (
            <button
              key={item?.id}
              onClick={item?.action}
              disabled={item?.disabled || item?.loading}
              className={`p-4 border border-border rounded-lg hover:shadow-soft transition-all duration-200 text-left group ${
                item?.disabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`p-2 rounded-lg ${item?.color} group-hover:scale-110 transition-transform ${
                  item?.disabled ? 'opacity-50' : ''
                }`}>
                  {item?.loading ? (
                    <Icon name="Loader2" size={20} className="animate-spin" />
                  ) : (
                    <Icon name={item?.icon} size={20} />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-md font-medium text-foreground mb-1">{item?.title}</h3>
                  <p className="text-sm text-muted-foreground">{item?.description}</p>
                  {item?.disabled && (
                    <p className="text-xs text-warning mt-1">Not assigned yet</p>
                  )}
                </div>
                {!item?.disabled && (
                  <Icon name="ArrowRight" size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                )}
              </div>
            </button>
          ))}
        </div>
      </div> */}

      {/* Recent Notifications - Load from Database */}
      {/* <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground">Recent Notifications</h2>
          <Button 
            variant="ghost" 
            size="sm" 
            iconName="Eye" 
            iconPosition="left"
            onClick={handleViewAllNotifications}
          >
            View All
          </Button>
        </div>
        
        {loadingNotifications ? (
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading notifications...</span>
          </div>
        ) : notifications?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Bell" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No recent notifications</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {notifications?.map((notification) => (
                <div key={notification?.id} className="p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-start space-x-3">
                    <div className={`p-1.5 rounded-full ${getPriorityColor(notification?.type)}`}>
                      <Icon name={getNotificationIcon(notification?.type)} size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-medium text-foreground">
                          {notification?.title}
                        </h4>
                        <span className="text-xs text-muted-foreground">
                          {formatTimeAgo(notification?.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {notification?.message}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t border-border">
              <Button 
                variant="outline" 
                size="sm" 
                fullWidth
                onClick={handleViewAllNotifications}
              >
                View All Notifications
              </Button>
            </div>
          </>
        )}
      </div> */}

      {/* Project Info */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4">Project Information</h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Building2" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">City</span>
            </div>
            <span className="text-sm text-muted-foreground">{project?.city}</span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="User" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Manager</span>
            </div>
            <span className="text-sm text-muted-foreground">{project?.manager}</span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Calendar" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Project Deadline</span>
            </div>
            <span className="text-sm text-muted-foreground">{project?.final_due_date}</span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Tag" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Category</span>
            </div>
            <span className="text-sm text-muted-foreground">{project?.category}</span>
          </div>
        </div>
      </div>

      {/* Problem Statement Modal */}
      {showProblemStatementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon name="FileText" size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">Problem Statement</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {team?.teamName || 'Team Problem Statement'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowProblemStatementModal(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Problem Statement Text */}
              <div>
                <h4 className="text-lg font-semibold text-foreground mb-3">Problem Description</h4>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-foreground leading-relaxed whitespace-pre-wrap">
                    {team?.problemStatement || project?.problem_statement || 'No problem statement available'}
                  </p>
                </div>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Building2" size={16} className="text-primary" />
                    <h5 className="text-sm font-semibold text-foreground">City</h5>
                  </div>
                  <p className="text-sm text-muted-foreground">{project?.city || team?.city || 'N/A'}</p>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Tag" size={16} className="text-primary" />
                    <h5 className="text-sm font-semibold text-foreground">Category</h5>
                  </div>
                  <p className="text-sm text-muted-foreground">{project?.category || team?.category || 'N/A'}</p>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="User" size={16} className="text-primary" />
                    <h5 className="text-sm font-semibold text-foreground">Manager</h5>
                  </div>
                  <p className="text-sm text-muted-foreground">{project?.manager || 'N/A'}</p>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Calendar" size={16} className="text-primary" />
                    <h5 className="text-sm font-semibold text-foreground">Deadline</h5>
                  </div>
                  <p className="text-sm text-muted-foreground">{project?.deadline || team?.finalDueDate || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-card border-t border-border p-6 flex justify-end">
              <Button
                variant="default"
                onClick={() => setShowProblemStatementModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* All Notifications Modal */}
      <NotificationsModal 
        isOpen={showNotificationsModal}
        onClose={() => setShowNotificationsModal(false)}
        teamId={team?.id}
      />
    </div>
  );
};

export default QuickActions;